// 会话实体表 会话的最原始定义信息
import { DB, ExecuteResult, SQLBatchTuple } from './index';
import time from '../time';
import log from '../log';

const upsert_sql =
    'insert into t_topic(' +
    'tid, uid, name, cid, ctime, ' +
    'type, sid, hidemid, ord, lv_time, ' +
    'ls_mid, remark, mute, lutime, lctime, unread, msg_synced, bridge_state, bridge_cursor, bridge_pages, bridge_epoch, bridge_updated_at' +
    ') values(' +
    '?, ?, ?, ?, ?, ' +
    '?, ?, ?, ?, ?, ' +
    '?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?' +
    ') ON CONFLICT(tid) DO UPDATE SET ' +
    'uid=?, name=?, cid=?, ctime=?, ' +
    'type=?, sid=?, hidemid=?, ord=?, lv_time=?, ' +
    'ls_mid=?, remark=?, mute=?, lutime=?, unread=?';

// 添加会话信息
export async function topic_upsert(
    db: DB,
    topic: Topic,
): Promise<ExecuteResult> {
    const t = TopicToEntity(fixTopic(topic));
    t.lctime = time.toUnixMillSecond(time.now());
    t.lutime = t.lctime;
    const params = [];
    // insert
    params.push(...[t.tid, t.uid, t.name, t.cid, t.ctime]);
    params.push(...[t.type, t.sid, t.hidemid, t.ord, t.lv_time]);
    params.push(...[t.ls_mid, t.remark, t.mute, t.lutime, t.lctime]);
    params.push(t.unread);
    params.push(t.msg_synced ?? 0);
    params.push(t.bridge_state ?? 0);
    params.push(t.bridge_cursor ?? '');
    params.push(t.bridge_pages ?? 0);
    params.push(t.bridge_epoch ?? 0);
    params.push(t.bridge_updated_at ?? 0);
    // update set（含 unread，便于全量 /sync/topic 每页刷新服务端未读；msg_synced 仍不在 UPDATE 中以免覆盖已拉历史标记）
    params.push(...[t.uid, t.name, t.cid, t.ctime]);
    params.push(...[t.type, t.sid, t.hidemid, t.ord, t.lv_time]);
    params.push(...[t.ls_mid, t.remark, t.mute, t.lutime, t.unread]);
    const r = await db.Execute(upsert_sql, params);
    if (r.error) {
        log.error('添加会话', JSON.stringify(topic), '信息失败', r.error);
    }
    return r;
}

/** 批量写入会话信息 */
export async function topic_batch_upsert(db: DB, topics: Topic[]): Promise<{ error: any }> {
    const commands: SQLBatchTuple[] = [];
    const timeUNIX = time.toUnixMillSecond(time.now());
    for (const topic of topics) {
        const t = TopicToEntity(fixTopic(topic));
        t.lctime = timeUNIX;
        t.lutime = timeUNIX;
        const params: any[] = [];
        // insert
        params.push(...[t.tid, t.uid, t.name, t.cid, t.ctime]);
        params.push(...[t.type, t.sid, t.hidemid, t.ord, t.lv_time]);
        params.push(...[t.ls_mid, t.remark, t.mute, t.lutime, t.lctime]);
        params.push(t.unread);
        params.push(t.msg_synced ?? 0);
        params.push(t.bridge_state ?? 0);
        params.push(t.bridge_cursor ?? '');
        params.push(t.bridge_pages ?? 0);
        params.push(t.bridge_epoch ?? 0);
        params.push(t.bridge_updated_at ?? 0);
        // update set（含 unread，与 topic_upsert 一致）
        params.push(...[t.uid, t.name, t.cid, t.ctime]);
        params.push(...[t.type, t.sid, t.hidemid, t.ord, t.lv_time]);
        params.push(...[t.ls_mid, t.remark, t.mute, t.lutime, t.unread]);
        commands.push([upsert_sql, params]);
    }
    if (commands.length === 0) return { error: null };
    const r = await db.BatchExecute(commands);
    return { error: 'topic_batch_upsert error' };
}
export async function topic_query_one(
    db: DB,
    tid: string,
): Promise<{ error: any; topic?: Topic }> {
    const query_one_sql =
        'select ' +
        'tid, uid, name, cid, ctime, ' +
        'type, sid, hidemid, ord, lv_time, ' +
        'ls_mid, remark, mute, draft, lutime, lctime, unread, msg_synced, bridge_state, bridge_cursor, bridge_pages, bridge_epoch, bridge_updated_at' +
        ` from t_topic where tid=? limit 1;`;
    const r = await db.Query(query_one_sql, [tid]);
    if (r.error) {
        return { error: r.error };
    }
    if (!(r.data && r.data.length > 0)) {
        return { error: null };
    }
    const d = r.data[0] as Entity;
    return { error: null, topic: EntityToTopic(d) };
}

const query_sql_prefix =
    'select ' +
    'tid, uid, name, cid, ctime, ' +
    'type, sid, hidemid, ord, lv_time, ' +
    'ls_mid, remark, mute, draft, lutime, lctime, unread, msg_synced, bridge_state, bridge_cursor, bridge_pages, bridge_epoch, bridge_updated_at' +
    ' from t_topic where';

// 查询置顶的会话
export async function topic_query_top(
    db: DB,
): Promise<{ error: any; topics?: Topic[] }> {
    const sqlstr = query_sql_prefix + ' ord > 0 order by ord desc';
    return topic_query_muti(db, sqlstr, []);
}

// 查询非置顶的会话
export async function topic_query_normal(
    db: DB,
    limit: number = 0, // 0 表示不限制数量
): Promise<{ error: any; topics?: Topic[] }> {
    let sqlstr = query_sql_prefix + ` ord <= 0 order by lv_time desc`;
    const params: any[] = [];
    if (limit > 0) {
        sqlstr += ` limit ?`;
        params.push(limit);
    }
    return topic_query_muti(db, sqlstr, params);
}

// 根据tid查询会话信息
export async function topic_query_muti(
    db: DB,
    sqlstr: string,
    params: any[],
): Promise<{ error: any; topics?: Topic[] }> {
    const r = await db.Query(sqlstr, params);
    if (r.error) {
        return { error: r.error };
    }
    if (!(r.data && r.data.length > 0)) {
        return { error: null };
    }
    let topics: Topic[] = [];
    const n = r.data.length;
    for (let i = 0; i < n; i++) {
        const d = r.data[i] as Entity;
        topics.push(EntityToTopic(d));
    }
    return { error: null, topics: topics };
}

export type TopicModifyCond = {
    tid: string; // 必填，要被修改的topic
    name?: string; // 群聊复制一份名字过来，省得查一次
    hidemid?: string; // 用int8不用bool是因为将来可能有其它的显示模式 是否可见 用途：会话删除-visual变为1  新会话来了或有新消息等反正会话展示了hide为0
    order?: number; // 排序，越大优先级越高，初始0，用途：置顶/取消置顶,取的就是置顶操作时的系统时间 毫秒级别
    lvTime?: number; // 上次打开时间,用途：同order级别按visitTime时间排序
    lsMid?: string; // 展示了的 最大消息id，用途：会话计数
    remark?: string; // 备注
    draft?: string,// 补充草稿
    mute?: boolean; // 是否静音
    unread?: number; // 未读数（仅用于清零等显式更新场景，upsert 不覆盖）
    msg_synced?: number; // 消息是否已加载过历史
    bridgeState?: number; // 消息桥接状态 0-未开始 1-进行中 2-已完成
    bridgeCursor?: string; // 消息桥接游标（下一页 maxMsgId）
    bridgePages?: number; // 消息桥接已拉页数
    bridgeEpoch?: number; // 消息桥接所属批量轮次
    bridgeUpdatedAt?: number; // 消息桥接状态更新时间
};

export async function topic_modify(
    db: DB,
    cond: TopicModifyCond,
): Promise<ExecuteResult> {
    const fields = [];
    const params = [];
    if (cond.name !== undefined) {
        fields.push('name=?');
        params.push(cond.name);
        // fields.push(`name='${cond.name}'`);
    }

    if (cond.hidemid !== undefined) {
        // fields.push(`hidemid='${cond.hidemid}'`);
        fields.push('hidemid=?');
        params.push(cond.hidemid);
    }
    if (cond.order !== undefined) {
        // fields.push(` ord=${cond.order}`);
        fields.push('ord=?');
        params.push(cond.order);
    }
    if (cond.lvTime !== undefined) {
        // fields.push(` lv_time=${cond.lvTime}`);
        fields.push('lv_time=?');
        params.push(cond.lvTime);
    }
    if (cond.lsMid !== undefined && cond.lsMid.length > 0) {
        // fields.push(` ls_mid='${cond.lsMid}'`);
        fields.push('ls_mid=?');
        params.push(cond.lsMid);
    }
    if (cond.remark !== undefined) {
        // fields.push(` remark='${cond.remark}'`);
        fields.push('remark=?');
        params.push(cond.remark);
    }
    if (cond.mute !== undefined) {
        // fields.push(` mute=${cond.mute ? 1 : 0}`);
        fields.push('mute=?');
        params.push(cond.mute ? 1 : 0);
    }
    if (cond.draft !== undefined) {
        // fields.push(` draft='${cond.draft}'`);
        fields.push('draft=?');
        params.push(cond.draft);
    }
    if (cond.unread !== undefined) {
        fields.push('unread=?');
        params.push(cond.unread);
    }
    if (cond.msg_synced !== undefined) {
        fields.push('msg_synced=?');
        params.push(cond.msg_synced);
    }
    if (cond.bridgeState !== undefined) {
        fields.push('bridge_state=?');
        params.push(cond.bridgeState);
    }
    if (cond.bridgeCursor !== undefined) {
        fields.push('bridge_cursor=?');
        params.push(cond.bridgeCursor);
    }
    if (cond.bridgePages !== undefined) {
        fields.push('bridge_pages=?');
        params.push(cond.bridgePages);
    }
    if (cond.bridgeEpoch !== undefined) {
        fields.push('bridge_epoch=?');
        params.push(cond.bridgeEpoch);
    }
    if (cond.bridgeUpdatedAt !== undefined) {
        fields.push('bridge_updated_at=?');
        params.push(cond.bridgeUpdatedAt);
    }
    if (fields.length <= 0) {
        log.warn('topic_modify 没有有效更新字段 cond:', JSON.stringify(cond));
        return { error: null, rowsAffected: 0, insertId: 0 };
    }
    params.push(cond.tid);
    const sql = 'update t_topic set ' + fields.join(',') + ` where tid= ? `;
    const r = await db.Execute(sql, params);
    return r;
}

/** 标记会话消息已加载过历史（QryLatest 成功后调用） */
export async function topic_update_msg_synced(db: DB, tid: string): Promise<ExecuteResult> {
    // 未读展示改回 msg_count + ls_mid；清零列上累计值，避免与「已拉历史」语义混用
    const sql = `update t_topic set msg_synced = 1, unread = 0, bridge_state = 2, bridge_cursor = '', bridge_pages = 0, bridge_updated_at = ? where tid = ?`;
    const now = time.toUnixMillSecond(time.now());
    return await db.Execute(sql, [now, tid]);
}

/**
 * 在尚未标记 msg_synced=1（未拉齐历史）时，为增量新消息累加未读。
 * 全量阶段 t_topic.unread 由 /sync/topic 写入；此处与「只显示 lastMsg」场景配套。
 */
export async function topic_incr_unread_if_partial(
    db: DB,
    tid: string,
    delta: number,
): Promise<ExecuteResult> {
    if (!tid || delta <= 0) {
        return { error: null, rowsAffected: 0, insertId: 0 };
    }
    const sql = `UPDATE t_topic SET unread = COALESCE(unread, 0) + ? WHERE tid = ? AND (msg_synced IS NULL OR msg_synced != 1)`;
    return await db.Execute(sql, [delta, tid]);
}

export async function topic_del(
    db: DB,
    tid: string,
): Promise<ExecuteResult> {
    const sql = `delete from t_topic where tid = ?`;
    return await db.Execute(sql, [tid]);
}
export async function topic_del_all(
    db: DB,
): Promise<ExecuteResult> {
    const sql = `delete from t_topic`;
    return await db.Execute(sql, []);
}

export type Topic = {
    tid: string; // 会话Id
    uid: number; // 群聊就是创建人userId 单聊就是关联的对方userId
    name: string; // 这个字段无用 会话名 群聊就是群名
    cid: string; // 客户端创建时填的id 创建人是自己时返回
    ctime: number; // 创建时间
    type: string; // 会话类型 群聊? 单聊?
    sid: number; // 同步Id 越大表示最近更新
    // 0430补充
    hidemid: string; // 设置隐藏时会话的消息id，后面收到更大的消息ID，则会话就又不隐藏
    order: number; // 排序，越大优先级越高，初始0，用途：置顶/取消置顶,取的就是置顶操作时的系统时间 毫秒级别
    lvTime: number; // 上次打开时间,用途：同order级别按visitTime时间排序
    lsMid: string; // 展示了的 最大消息id，用途：会话计数
    remark: string; // 备注
    mute: boolean; // 是否静音
    draft: string; // 补充草稿
    unread: number; // 未读：msg_synced=1 时由 get_summary 的 msg_count 展示；msg_synced≠1 时由全量 /sync/topic 写入本列，增量 sync 在他人消息落库时 +1
    msg_synced: number; // 消息是否已加载过历史（0-未加载 1-已加载，仅 INSERT 时写入，upsert 不覆盖）
    bridgeState: number; // 消息桥接状态 0-未开始 1-进行中 2-已完成
    bridgeCursor: string; // 消息桥接游标（下一页 maxMsgId）
    bridgePages: number; // 消息桥接已拉页数
    bridgeEpoch: number; // 消息桥接所属批量轮次
    bridgeUpdatedAt: number; // 消息桥接状态更新时间
    // 本地字段
    lutime: number; // 本地 更新时间
    lctime: number; // 本地 创建时间
};

function fixTopic(t: Topic): Topic {
    return Object.assign({}, defaultTopic, t);
}

export const defaultTopic: Topic = {
    tid: '', // 会话Id
    uid: 0, // 群聊就是创建人userId 单聊就是关联的对方userId
    name: '', // 会话名 群聊就是群名
    cid: '', // 客户端创建时填的id 创建人是自己时返回
    ctime: 0, // 创建时间
    type: '', // 会话类型 群聊? 单聊?
    sid: 0, // 同步Id 越大表示最近更新
    // 0430补充
    hidemid: '', // 用int8不用bool是因为将来可能有其它的显示模式 是否可见 用途：会话删除-visual变为1  新会话来了或有新消息等反正会话展示了hide为0
    order: 0, // 排序，越大优先级越高，初始0，用途：置顶/取消置顶,取的就是置顶操作时的系统时间 毫秒级别
    lvTime: 0, // 上次打开时间,用途：同order级别按visitTime时间排序
    lsMid: '', // 展示了的 最大消息id，用途：会话计数
    remark: '', // 备注
    mute: false, // 是否静音
    draft: '',// 补充草稿
    unread: 0, // 未读消息数
    msg_synced: 0, // 消息是否已加载过历史
    bridgeState: 0, // 消息桥接状态 0-未开始 1-进行中 2-已完成
    bridgeCursor: '', // 消息桥接游标（下一页 maxMsgId）
    bridgePages: 0, // 消息桥接已拉页数
    bridgeEpoch: 0, // 消息桥接所属批量轮次
    bridgeUpdatedAt: 0, // 消息桥接状态更新时间
    // 本地字段
    lutime: 0, // 本地 更新时间
    lctime: 0, // 本地 创建时间
};

type Entity = {
    tid: string; // 会话Id
    uid: number; // 群聊就是创建人userId 单聊就是关联的对方userId
    name: string; // 会话名 群聊就是群名
    cid: string; // 客户端创建时填的id 创建人是自己时返回
    ctime: number; // 创建时间
    type: string; // 会话类型 群聊? 单聊?
    sid: number; // 同步Id 越大表示最近更新
    // 0430补充
    hidemid: string; // 用int8不用bool是因为将来可能有其它的显示模式 是否可见 用途：会话删除-visual变为1  新会话来了或有新消息等反正会话展示了hide为0
    ord: number; // 排序，越大优先级越高，初始0，用途：置顶/取消置顶,取的就是置顶操作时的系统时间 毫秒级别
    lv_time: number; // 上次打开时间,用途：同order级别按visitTime时间排序
    ls_mid: string; // 展示了的 最大消息id，用途：会话计数
    remark: string; // 备注
    mute: number; // 是否静音 0 - 静音 1 - 静音
    draft: string,// 补充草稿
    unread: number; // 未读消息数
    msg_synced: number; // 消息是否已加载过历史
    bridge_state: number; // 消息桥接状态
    bridge_cursor: string; // 消息桥接游标（下一页 maxMsgId）
    bridge_pages: number; // 消息桥接已拉页数
    bridge_epoch: number; // 消息桥接所属批量轮次
    bridge_updated_at: number; // 消息桥接状态更新时间
    // 本地字段
    lutime: number; // 本地 更新时间
    lctime: number; // 本地 创建时间
};

function TopicToEntity(a: Topic): Entity {
    const b: Entity = {
        tid: a.tid, // 会话Id
        uid: a.uid, // 群聊就是创建人userId 单聊就是关联的对方userId
        name: a.name, // 会话名 群聊就是群名
        cid: a.cid, // 客户端创建时填的id 创建人是自己时返回
        ctime: a.ctime, // 创建时间
        type: a.type, // 会话类型 群聊? 单聊?
        sid: a.sid, // 同步Id 越大表示最近更新
        hidemid: a.hidemid, // 用int8不用bool是因为将来可能有其它的显示模式 是否可见 用途：会话删除-visual变为1  新会话来了或有新消息等反正会话展示了hide为0
        ord: a.order, // 排序，越大优先级越高，初始0，用途：置顶/取消置顶,取的就是置顶操作时的系统时间 毫秒级别
        lv_time: a.lvTime, // 上次打开时间,用途：同order级别按visitTime时间排序
        ls_mid: a.lsMid, // 展示了的 最大消息id，用途：会话计数
        remark: a.remark, // 备注
        mute: a.mute ? 1 : 0,
        draft: a.draft,// 补充草稿
        unread: a.unread || 0, // 未读消息数
        msg_synced: a.msg_synced ?? 0, // 消息是否已加载过历史
        bridge_state: a.bridgeState ?? 0,
        bridge_cursor: a.bridgeCursor || '',
        bridge_pages: a.bridgePages ?? 0,
        bridge_epoch: a.bridgeEpoch ?? 0,
        bridge_updated_at: a.bridgeUpdatedAt ?? 0,
        lutime: a.lutime, // 本地 更新时间
        lctime: a.lctime, // 本地 创建时间
    };
    return b;
}

function EntityToTopic(a: Entity): Topic {
    const b: Topic = {
        tid: a.tid, // 会话Id
        uid: a.uid, // 群聊就是创建人userId 单聊就是关联的对方userId
        name: a.name, // 会话名 群聊就是群名
        cid: a.cid, // 客户端创建时填的id 创建人是自己时返回
        ctime: a.ctime, // 创建时间
        type: a.type, // 会话类型 群聊? 单聊?
        sid: a.sid, // 同步Id 越大表示最近更新
        // 0430补充
        hidemid: a.hidemid, // 用int8不用bool是因为将来可能有其它的显示模式 是否可见 用途：会话删除-visual变为1  新会话来了或有新消息等反正会话展示了hide为0
        order: a.ord, // 排序，越大优先级越高，初始0，用途：置顶/取消置顶,取的就是置顶操作时的系统时间 毫秒级别
        lvTime: a.lv_time, // 上次打开时间,用途：同order级别按visitTime时间排序
        lsMid: a.ls_mid, // 展示了的 最大消息id，用途：会话计数
        remark: a.remark, // 备注
        mute: a.mute === 1 ? true : false,
        draft: a.draft,// 补充草稿
        unread: a.unread || 0, // 未读消息数
        msg_synced: a.msg_synced ?? 0, // 消息是否已加载过历史
        bridgeState: a.bridge_state ?? 0,
        bridgeCursor: a.bridge_cursor || '',
        bridgePages: a.bridge_pages ?? 0,
        bridgeEpoch: a.bridge_epoch ?? 0,
        bridgeUpdatedAt: a.bridge_updated_at ?? 0,
        // 本地字段
        lutime: a.lutime, // 本地 更新时间
        lctime: a.lctime, // 本地 创建时间
    };
    return b;
}
